<template>
  <div class="product_filter">
    <div class="page-wrapper">
      <FilterContent
        ref="content"
        view-options
        :category="category ? category : {}"
        :related-posts-ids="category ? [category.id] : []"
        :results-entries="resultsEntries"
        results-entry-type="product"
        :results-header="$t('Products')"
        :seomatic="seomatic"
        :sort-by-default="category ? category.sortByDefault : null"
        :sort-by-attributes="category ? category.sortingAttributes : []"
        :sort-by-additional-options="category ? category.sortByOptions : []"
        v-if="resultsEntries"
      >
        <template #header>
          <div>
            <header class="filter_content__header">
              <Slider autoplay show-indicators v-if="category && category.headerImages && category.headerImages.length">
                <template #slides>
                  <div v-for="(item, index) in category.headerImages" :key="index">
                    <MediaImage
                      background
                      :alt="item.title"
                      :sources="[
                        { srcset: item.pageHeader.srcsetWebp, type: 'image/webp' },
                        { srcset: item.pageHeader.srcset },
                      ]"
                    />

                    <Flex center class="c_box--mask">
                      <h1 class="c_2022--h_cl c_2022--h1_schnyder c_text--white">{{ category.title }}</h1>
                    </Flex>
                  </div>
                </template>
              </Slider>

              <section class="c_page_header c_wrapper_2022">
                <RelativeAnchor :id="category ? category.title : $t('Products')" />
                <div>
                  <h1 class="c_2022--h_cl c_2022--h1_schnyder">
                    <span class="schnyder_break">{{ category ? category.title : $t('Products') }}</span>
                    <div class="c_page_header__divider" role="presentation"></div>
                  </h1>
                </div>
              </section>
            </header>
          </div>
        </template>
      </FilterContent>
    </div>
  </div>
</template>

<script>
import {
  defineComponent,
  reactive,
  toRefs,
  useContext,
  useFetch,
  useMeta,
  useRoute,
  useRouter,
  useStore,
} from '@nuxtjs/composition-api';
import { log } from 'JS/global.js';
import { gqlToObject } from 'JS/seomatic.js';
import {
  pageQuery,
  resultsQuery,
  categoryDescendantsQuery,
  resultsByCategoryIdsQuery,
} from 'GQL/pages/filter_pages/productFilterGql.js';

import FilterContent from 'Components/New_FilterP/FilterContent.vue';
import Flex from 'Components/flex/Flex.vue';
import MediaImage from 'Components/image/MediaImage.vue';
import Slider from 'Components/slider/Slider.vue';
import RelativeAnchor from 'Components/relative_anchor/RelativeAnchor.vue';

export default defineComponent({
  name: 'PageProductFilter',
  head: {},
  components: {
    FilterContent,
    Flex,
    MediaImage,
    Slider,
    RelativeAnchor,
  },
  setup() {
    const { app, $craft, payload, $preview } = useContext();
    const route = useRoute();
    const router = useRouter();
    const store = useStore();

    const state = reactive({
      category: null,
      resultsEntries: null,
      seomatic: null,
    });

    // Flatten nested product filter objects into `parent:child` strings for easier matching
    const addFormattedFilters = (entries = []) => {
      entries.forEach((item) => {
        const formattedFilters = [];
        (item.productFilters || []).forEach((filter) => {
          formattedFilters.push(`${filter.parent.slug}:${filter.slug}`);
        });
        item.formattedFilters = formattedFilters;
      });
      return entries;
    };

    useFetch(async () => {
      const p = route.value.params;
      const site = store.state.global.localeSites[app.i18n.locale].siteHandle;

      const isTopLevelPage =
        !!p.contentSection && !p.slug && !p.category1 && !p.category2 && !p.category3 && !p.category4 && !p.category5;

      // Top-level pages end with `/products`, deeper entries append all additional categories as url segments
      const uriPreferred = isTopLevelPage
        ? `${p.contentSection}/products`
        : [p.contentSection, 'products', p.category1, p.category2, p.category3, p.category4, p.category5, p.slug]
            .filter(Boolean)
            .join('/');

      if (isTopLevelPage) {
        const uriToMatch = `${p.contentSection}/products`;

        // 1) Fetch the category page content so the index page has the same fields as any other category page
        const pageRequest = await $craft({
          apiLog: 'PageProductFilter (pageQuery)',
          query: pageQuery,
          variables: { site, uri: uriToMatch },
        });

        if (pageRequest?.data?.category) {
          state.category = pageRequest.data.category;
          state.seomatic = pageRequest.data.category?.seomatic || null;
        } else {
          state.category = null;
          state.seomatic = null;
        }

        // If we can’t resolve the category page content, treat it as a 404
        if (!state.category?.id) {
          router.replace('/error/404');
          store.commit('global/updateGlobalDarkTheme', false);
          return;
        }

        // 2) Grab all descendant category IDs
        const descendantsRequest = await $craft({
          apiLog: 'PageProductFilter (category descendants)',
          query: categoryDescendantsQuery,
          variables: {
            site,
            id: state.category.id,
          },
        });

        const descendantIds = descendantsRequest?.data?.categories?.map((c) => c.id).filter(Boolean) || [];

        // Include entries assigned to the top-level category
        const categoryIds = Array.from(new Set([state.category.id, ...descendantIds]));

        // 3) Fetch only products that live somewhere under this category tree
        const resultsRequest = await $craft({
          apiLog: 'PageProductFilter (resultsByCategoryIdsQuery)',
          query: resultsByCategoryIdsQuery,
          variables: {
            site,
            ids: categoryIds,
          },
        });

        state.resultsEntries = addFormattedFilters(resultsRequest.data.entries);
        store.commit('global/updateGlobalDarkTheme', false);
        return;
      }

      // Nested category page
      let data;

      // In live preview we often get a payload already - use it and avoid a second request.
      if (!$preview && payload?.category) {
        data = payload;
      } else {
        const pageRequest = await $craft({
          apiLog: 'PageProductFilter',
          query: pageQuery,
          variables: { site, uri: uriPreferred },
        });

        if (pageRequest?.data) {
          data = pageRequest.data;
        }
      }

      if (!data?.category) {
        router.replace('/error/404');
        store.commit('global/updateGlobalDarkTheme', false);
        return;
      }

      state.category = data.category;
      state.seomatic = data.category?.seomatic || null;

      // Fetch the product entries that are explicitly related to this category page.
      const resultsRequest = await $craft({
        apiLog: 'PageProductFilter (resultsQuery)',
        query: resultsQuery,
        variables: {
          site,
          id: state.category.id,
        },
      });

      state.resultsEntries = addFormattedFilters(resultsRequest.data.entries);
      store.commit('global/updateGlobalDarkTheme', false);
    });

    useMeta(() => {
      return state.seomatic ? { ...gqlToObject(state.seomatic) } : {};
    });

    return { ...toRefs(state) };
  },

  mounted() {
    log('PageProductFilter loaded');
  },
});
</script>

<style lang="scss">
.product_filter {
  .c_page_header {
    $self: &;
    text-align: center;
    padding-top: px_to_rem(15);
    padding-bottom: px_to_rem(15);
    @include mq($mq_md_to_lg) {
      padding-top: px_to_rem(30);
      padding-bottom: px_to_rem(30);
    }
    h1 {
      position: relative;
      padding: 0 px_to_rem(20);
      @include mq($mq_sm_to_md) {
        padding: 0 px_to_rem(40);
      }
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      span {
        position: relative;
        z-index: 2;
        padding: 0 px_to_rem(10);
        @include mq($mq_sm_to_md) {
          padding: 0 px_to_rem(20);
        }
        background-color: $color_white;
        display: inline-block;
        white-space: pre-line;
      }
    }
    .schnyder_break {
      white-space: pre-line;
    }
    p {
      margin-top: px_to_rem(25);
    }
    @at-root #{&}__divider {
      z-index: 1;
      height: px_to_rem(1.5);
      width: 100%;
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      margin: auto;
      background-color: $color_yellow;
    }
  }
  .lp__block_2023.lp__block--pageHeader {
    padding: 0;
  }

  .custom-ul {
    color: white;
    list-style: disc;
    list-style-position: outside;
    padding-left: 16px;
  }

  .custom-ul li {
    color: white;
    list-style: disc;
    list-style-position: outside;
    text-indent: 0px;
  }
}
</style>
